import { ChangeDetectionStrategy, Component, HostBinding } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { ActivatedRoute } from '@angular/router';

import { CountItem, ItemType, LeagueItem, VirtualCompetitionGroupItem, children } from '@frontend/sports/common/core/data-access/sport-model';
import { HooksWireup, OnRouteResolve } from '@frontend/sports/common/core/utils/hooks';
import { StandingsData } from '@frontend/sports/grid/core/feature/model';
import { CompetitionRoute } from '@frontend/sports/navigation/core/feature/models';
import { UrlHelperService } from '@frontend/sports/navigation/core/feature/url-helper';
import { Observable, of } from 'rxjs';

import { VirtualCompetitionModel, VirtualCompetitionService } from '../competition-list/services/virtual-competition.service';
import { FixtureListHeaderComponent } from '../fixture-list/fixture-list-header.component';
import { TournamentGroupsComponent } from '../tournament-groups/tournament-groups.component';
import { StandingsPageResponse } from './standings-page.model';
import { StandingsComponent } from './standings.component';

@HooksWireup()
@Component({
    imports: [StandingsComponent, TournamentGroupsComponent, FixtureListHeaderComponent],
    selector: 'ms-standings-page',
    templateUrl: 'standings-page.component.html',
    changeDetection: ChangeDetectionStrategy.OnPush,
})
export class StandingsPageComponent extends OnRouteResolve<StandingsPageResponse> {
    @HostBinding('class') className = 'event-list';

    standingsData?: StandingsData = {
        groups: [],
    };

    sport?: CountItem;
    virtualCompetitionModel: VirtualCompetitionModel | null;
    competitionTitle?: string;

    constructor(
        route: ActivatedRoute,
        private virtualCompetitionService: VirtualCompetitionService,
        private urlHelperService: UrlHelperService,
    ) {
        super(route);
    }

    onRouteResolved(model: StandingsPageResponse): void {
        this.standingsData = model.standingsData;
        this.sport = model.sport;
        this.competitionTitle = model.route.league ? model.virtualCompetition?.name || undefined : undefined;
        this.getVirtualCompetition(model.sport, model.virtualCompetition, model.route)
            .pipe(takeUntilDestroyed(this.destroyRef))
            .subscribe((virtualCompetitionModel) => {
                this.virtualCompetitionModel = virtualCompetitionModel;
            });
    }

    private getVirtualCompetition(
        sport: CountItem | undefined,
        virtualCompetition: LeagueItem | undefined,
        route: CompetitionRoute,
    ): Observable<VirtualCompetitionModel | null> {
        if (!sport || !virtualCompetition) {
            return of(null);
        }
        const league = children(sport, ItemType.Competition).pop() as LeagueItem;

        const getUrl = (urlGroup?: VirtualCompetitionGroupItem) => {
            const region = route.region && route.regionName ? { id: route.region, name: route.regionName } : undefined;

            return this.urlHelperService.getStandingsUrl(sport, region, league, urlGroup);
        };

        return this.virtualCompetitionService.subscribe$(sport, virtualCompetition, getUrl);
    }

    get isFavouriteVisible(): boolean {
        return !this.virtualCompetitionModel || !!this.virtualCompetitionModel.realCompetitionId;
    }
}
